// frontend/src/hooks/useProjects.ts - COMPLETE PROJECT-SPECIFIC HOOKS
// Entity-specific hooks for projects with ALL missing functionality restored
// Includes correspondence relationships, analytics, archiving, and advanced features

import { useCallback } from 'react';
import apiClient from '@/api';
import { useAuthStore } from '@/store/auth';
import { 
  useApi, 
  useMutation, 
  validateObjectId, 
  validateObjectIds, 
  normalizeSingleResponse, 
  normalizePaginatedResponse,
  type UseApiOptions,
  type UseMutationOptions
} from './useApi';

// Import only types from API - not functions
import type {
  // Response types
  StandardResponse,
  PaginatedResponse,
  
  // Project types
  Project,
  ProjectCreateRequest as BaseProjectCreateRequest,
  ProjectUpdateRequest as BaseProjectUpdateRequest,
  ProjectQueryParams,
  
  // Correspondence types
  Correspondence,
  CorrespondenceQueryParams,
  
  // Query parameter types
  QueryParams,
  Priority,
  NormalizedUser
} from '@/api/types';

// ===== EXTENDED PROJECT TYPES =====

/**
 * Extended ProjectCreateRequest with organizationId for implementor usage
 */
export interface ProjectCreateRequest extends BaseProjectCreateRequest {
  organizationId?: string;
  projectName?: string; // Backend compatibility
  department?: string; // Extended property
  conditions?: any; // Extended property
  exclusionConditions?: any; // Extended property
  keywords?: string[]; // Extended property
  conditionType?: 'union' | 'intersection'; // Extended property
  scope?: 'organization' | 'user'; // Extended property
}

/**
 * Extended ProjectUpdateRequest with organizationId and additional fields
 */
export interface ProjectUpdateRequest extends BaseProjectUpdateRequest {
  organizationId?: string;
  projectName?: string; // Backend compatibility
  department?: string; // Extended property
  conditions?: any; // Extended property
  exclusionConditions?: any; // Extended property
  keywords?: string[]; // Extended property
  conditionType?: 'union' | 'intersection'; // Extended property
  scope?: 'organization' | 'user'; // Extended property
}

// ✅ FIXED: Define project-specific priority type to match backend validation
export type ProjectPriority = 'low' | 'normal' | 'high' | 'critical';

// ===== BACKEND RESPONSE INTERFACES =====

/**
 * Backend projects list response format
 */
interface BackendProjectListResponse {
  projects?: Project[];
  data?: Project[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage?: boolean;
    hasPrevPage?: boolean;
    currentPage?: number;
    itemsPerPage?: number;
  };
  total?: number;
  totalPages?: number;
  page?: number;
  limit?: number;
  analytics?: any;
  filters?: any;
}

/**
 * Backend single project response format
 */
interface BackendProjectResponse {
  success?: boolean;
  data?: Project;
  project?: Project;
  message?: string;
  error?: string;
  analytics?: any;
}

/**
 * Backend project analytics response format
 */
interface BackendProjectAnalyticsResponse {
  project: {
    id: string;
    name: string;
  };
  period: {
    startDate: string;
    endDate: string;
    duration: string;
  };
  correspondences: {
    total: number;
    manual: number;
    automatic: number;
    avgClassificationScore: number;
  };
  responses: Record<string, number>;
  timeline: Array<{
    date: string;
    total: number;
    manual: number;
    automatic: number;
  }>;
  topKeywords: Array<{
    keyword: string;
    count: number;
  }>;
}

// ===== TYPE GUARDS =====

function isBackendProjectListResponse(response: any): response is BackendProjectListResponse {
  return response && 
    typeof response === 'object' && 
    (Array.isArray(response.projects) || Array.isArray(response.data));
}

function isBackendProjectResponse(response: any): response is BackendProjectResponse {
  return response && 
    typeof response === 'object' && 
    (response.data || response.project || response._id || response.name);
}

function isBackendProjectAnalyticsResponse(response: any): response is BackendProjectAnalyticsResponse {
  return response && 
    typeof response === 'object' && 
    response.project &&
    response.correspondences &&
    typeof response.correspondences.total === 'number';
}

// ===== UTILITY FUNCTIONS =====

/**
 * Normalize visibility groups from Group[] | string[] to string[]
 */
const normalizeVisibilityGroups = (groups: string[] | any[] | undefined): string[] => {
  if (!groups || !Array.isArray(groups)) {
    return [];
  }
  
  return groups.map((group: any) => {
    if (typeof group === 'string') {
      return group;
    }
    if (typeof group === 'object' && group !== null) {
      return String((group as any)._id || (group as any).id || '');
    }
    return String(group || '');
  }).filter((id: string) => id && id.length > 0);
};

/**
 * ✅ CRITICAL: Transform project data for backend API compatibility
 */
const transformProjectForBackend = (data: any): any => {
  
  const transformed = {
    // ✅ Backend expects 'projectName' field
    projectName: data.name || data.projectName,
    
    // ✅ Also include 'name' for frontend compatibility
    name: data.name || data.projectName,
    
    description: data.description,
    
    // ✅ FIXED: Priority mapping - convert 'medium' to 'normal'
    priority: data.priority === 'medium' ? 'normal' : (data.priority || 'normal'),
    
    isActive: data.isActive !== undefined ? data.isActive : true,
    isPublic: data.isPublic || false,
    visibilityGroups: normalizeVisibilityGroups(data.visibilityGroups),
    
    // Extended properties (if provided)
    ...(data.department && { department: data.department }),
    ...(data.conditions && { conditions: data.conditions }),
    ...(data.exclusionConditions && { exclusionConditions: data.exclusionConditions }),
    ...(data.keywords && Array.isArray(data.keywords) && data.keywords.length > 0 && { keywords: data.keywords }),
    ...(data.conditionType && { conditionType: data.conditionType }),
    ...(data.scope && { scope: data.scope }),
    
    // Organization ID for API routing
    ...(data.organizationId && { organizationId: data.organizationId })
  };
  
  return transformed;
};

/**
 * Validate project data before API calls
 */
const validateProjectData = (data: any): void => {
  if (!data.name && !data.projectName) {
    throw new Error('Project name is required');
  }
  
  if (data.name && data.name.length > 200) {
    throw new Error('Project name cannot exceed 200 characters');
  }
  
  if (data.description && data.description.length > 2000) {
    throw new Error('Description cannot exceed 2000 characters');
  }
  
  if (data.department && data.department.length > 100) {
    throw new Error('Department name cannot exceed 100 characters');
  }
  
  // ✅ FIXED: Validate priority with correct values
  const validPriorities: ProjectPriority[] = ['low', 'normal', 'high', 'critical'];
  if (data.priority && !validPriorities.includes(data.priority) && data.priority !== 'medium') {
    throw new Error('Priority must be low, normal, high, or critical');
  }
  
  // Validate visibility groups if provided
  if (data.visibilityGroups && Array.isArray(data.visibilityGroups)) {
    data.visibilityGroups.forEach((groupId: string, index: number) => {
      try {
        validateObjectId(groupId, `Visibility group ${index + 1}`);
      } catch (error: any) {
        throw new Error(`Invalid visibility group ID at index ${index}: ${error.message}`);
      }
    });
  }
};

// ===== PROJECT QUERY HOOKS =====

/**
 * Fetch paginated list of projects - FIXED REFRESH ISSUE
 */
export function useProjects(params?: ProjectQueryParams, options?: UseApiOptions) {
  const { user: currentUser } = useAuthStore();
  
  return useApi(
    async (): Promise<PaginatedResponse<Project>> => {
      
      // Check if projects API exists
      if (!apiClient.projects || !('list' in apiClient.projects)) {
        throw new Error('Projects API not available in current API version');
      }
      
      const response = await (apiClient.projects as any).list(params);
      
      // ✅ CRITICAL FIX: Handle different backend response formats
      
      // Case 1: Raw array response (what you're currently getting)
      if (Array.isArray(response)) {
        
        // Transform raw array to expected format
        const transformedResponse: PaginatedResponse<Project> = {
          data: response,  // Use the raw array as data
          pagination: {
            currentPage: 1,
            totalPages: 1,
            totalItems: response.length,
            itemsPerPage: response.length,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: response.length,
          totalPages: 1,
          page: 1,
          limit: response.length
        };
        
        
        return transformedResponse;
      }
      
      // Case 2: Object response with projects or data array
      if (isBackendProjectListResponse(response)) {
        let projects: Project[] = [];
        let pagination = {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPrevPage: false
        };
        
        // Extract projects array
        if (Array.isArray(response.projects)) {
          projects = response.projects;
        } else if (Array.isArray(response.data)) {
          projects = response.data;
        }
        
        // Extract pagination info
        if (response.pagination) {
          pagination = {
            currentPage: response.pagination.currentPage || response.pagination.page || 1,
            totalPages: response.pagination.totalPages || 1,
            totalItems: response.pagination.total || projects.length,
            itemsPerPage: response.pagination.itemsPerPage || response.pagination.limit || 10,
            hasNextPage: response.pagination.hasNextPage || false,
            hasPrevPage: response.pagination.hasPrevPage || false
          };
        } else {
          // Fallback pagination
          pagination.totalItems = response.total || projects.length;
          pagination.totalPages = response.totalPages || 1;
          pagination.currentPage = response.page || 1;
          pagination.itemsPerPage = response.limit || 10;
        }
        
        
        return {
          data: projects,
          pagination,
          total: pagination.totalItems,
          totalPages: pagination.totalPages,
          page: pagination.currentPage,
          limit: pagination.itemsPerPage
        };
      }
      
      // Case 3: Fallback - but this was failing before
      
      // If it's an object but not recognized format, try to extract data
      if (response && typeof response === 'object' && !Array.isArray(response)) {
        // Look for any array in the response
        const possibleArrays = Object.keys(response).filter(key => Array.isArray(response[key]));
        
        if (possibleArrays.length > 0) {
          const projects = response[possibleArrays[0]];
          return {
            data: projects,
            pagination: {
              currentPage: 1,
              totalPages: 1,
              totalItems: projects.length,
              itemsPerPage: projects.length,
              hasNextPage: false,
              hasPrevPage: false
            },
            total: projects.length,
            totalPages: 1,
            page: 1,
            limit: projects.length
          };
        }
      }
      
      // Final fallback - return empty
      console.warn('useProjects: Could not parse response, returning empty');
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPrevPage: false
        },
        total: 0,
        totalPages: 1,
        page: 1,
        limit: 10
      };
    },
    {
      immediate: true,
      // FIXED: Make sure params changes trigger re-fetch but avoid infinite loops
      dependencies: [
        JSON.stringify(params || {}),
        currentUser?.role,
        typeof currentUser?.organization === 'string' 
          ? currentUser.organization 
          : (currentUser?.organization as any)?._id || (currentUser?.organization as any)?.id || 'undefined'
      ],
      retryCount: 2,
      retryDelay: 1000,
      rateLimitKey: 'projects_list',
      ...options
    }
  );
}

/**
 * Fetch single project by ID
 */
export function useProject(id?: string, options?: UseApiOptions) {
  return useApi(
    async (): Promise<Project> => {
      validateObjectId(id, 'Project ID');
      
      
      if (!apiClient.projects || !('get' in apiClient.projects)) {
        throw new Error('Project retrieval not available in current API version');
      }
      
      const response = await (apiClient.projects as any).get(id!);
      
      // Handle different backend response formats
      if (isBackendProjectResponse(response)) {
        let project: Project | undefined;
        
        if (response.data) {
          project = response.data;
        } else if (response.project) {
          project = response.project;
        } else if ((response as any)._id || (response as any).name) {
          project = response as Project;
        }
        
        if (!project) {
          console.error('useProject: Could not extract project from response:', response);
          throw new Error('Invalid project data received from server');
        }
        
        return project;
      }
      
      return normalizeSingleResponse<Project>(response);
    },
    {
      immediate: !!id,
      dependencies: [id || ''],
      retryCount: 2,
      retryDelay: 1000,
      rateLimitKey: `project_${id || 'undefined'}`,
      ...options
    }
  );
}

/**
 * ✅ RESTORED: Fetch project correspondences with relationship data
 */
export function useProjectCorrespondences(projectId?: string, params?: CorrespondenceQueryParams, options?: UseApiOptions) {
  return useApi(
    async (): Promise<PaginatedResponse<Correspondence>> => {
      validateObjectId(projectId, 'Project ID');
      
      
      if (!apiClient.projects || !('getCorrespondences' in apiClient.projects)) {
        throw new Error('Project correspondences not available in current API version');
      }
      
      const response = await (apiClient.projects as any).getCorrespondences(projectId!, params);
      
      return normalizePaginatedResponse<Correspondence>(response);
    },
    {
      immediate: !!projectId,
      dependencies: [projectId || '', JSON.stringify(params || {})],
      retryCount: 1,
      retryDelay: 2000,
      rateLimitKey: `project_correspondences_${projectId || 'undefined'}`,
      ...options
    }
  );
}

/**
 * ✅ RESTORED: Get project statistics and metrics
 */
export function useProjectStats(projectId?: string, options?: UseApiOptions) {
  return useApi(
    async (): Promise<any> => {
      validateObjectId(projectId, 'Project ID');
      
      
      if (!apiClient.projects || !('getStats' in apiClient.projects)) {
        throw new Error('Project statistics not available in current API version');
      }
      
      const response = await (apiClient.projects as any).getStats(projectId!);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: false, // Manual trigger for stats
      dependencies: [projectId || ''],
      retryCount: 1,
      retryDelay: 2000,
      rateLimitKey: `project_stats_${projectId || 'undefined'}`,
      ...options
    }
  );
}

/**
 * ✅ RESTORED: Get project activity timeline
 */
export function useProjectActivity(projectId?: string, params?: QueryParams, options?: UseApiOptions) {
  return useApi(
    async (): Promise<PaginatedResponse<any>> => {
      validateObjectId(projectId, 'Project ID');
      
      
      if (!apiClient.projects || !('getActivity' in apiClient.projects)) {
        throw new Error('Project activity not available in current API version');
      }
      
      const response = await (apiClient.projects as any).getActivity(projectId!, params);
      
      return normalizePaginatedResponse(response);
    },
    {
      immediate: !!projectId,
      dependencies: [projectId || '', JSON.stringify(params || {})],
      retryCount: 1,
      retryDelay: 2000,
      rateLimitKey: `project_activity_${projectId || 'undefined'}`,
      ...options
    }
  );
}

/**
 * ✅ RESTORED: Search projects with advanced filtering
 */
export function useSearchProjects(query: string, filters?: Partial<ProjectQueryParams>, options?: UseApiOptions) {
  return useApi(
    async (): Promise<PaginatedResponse<Project>> => {
      if (!query.trim()) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      
      const searchParams = {
        search: query.trim(),
        ...filters,
        page: 1,
        limit: 20
      };
      
      if (!apiClient.projects || !('search' in apiClient.projects)) {
        // Fallback to regular list with search
        if (!apiClient.projects || !('list' in apiClient.projects)) {
          throw new Error('Projects search not available in current API version');
        }
        const response = await (apiClient.projects as any).list(searchParams);
        return normalizePaginatedResponse<Project>(response);
      }
      
      const response = await (apiClient.projects as any).search(searchParams);
      
      return normalizePaginatedResponse<Project>(response);
    },
    {
      immediate: false,
      dependencies: [query, JSON.stringify(filters || {})],
      retryCount: 1,
      retryDelay: 2000,
      debounce: 500,
      rateLimitKey: `projects_search_${query}`,
      ...options
    }
  );
}

/**
 * ✅ RESTORED: Get comprehensive project analytics
 */
export function useProjectAnalytics(projectId?: string, params?: { period?: string; startDate?: string; endDate?: string }, options?: UseApiOptions) {
  return useApi(
    async (): Promise<BackendProjectAnalyticsResponse> => {
      validateObjectId(projectId, 'Project ID');
      
      
      if (!apiClient.projects || !('getAnalytics' in apiClient.projects)) {
        throw new Error('Project analytics not available in current API version');
      }
      
      const response = await (apiClient.projects as any).getAnalytics(projectId!, params);
      
      if (isBackendProjectAnalyticsResponse(response)) {
        return response;
      }
      
      return normalizeSingleResponse<BackendProjectAnalyticsResponse>(response);
    },
    {
      immediate: false, // Manual trigger for analytics
      dependencies: [projectId || '', JSON.stringify(params || {})],
      retryCount: 1,
      retryDelay: 2000,
      rateLimitKey: `project_analytics_${projectId || 'undefined'}`,
      ...options
    }
  );
}

// ===== PROJECT MUTATION HOOKS =====

/**
 * Create new project with proper field mapping
 */
export function useCreateProject(options?: UseMutationOptions<Project, ProjectCreateRequest>) {
  const { user: currentUser } = useAuthStore();
  
  return useMutation(
    async (data: ProjectCreateRequest): Promise<Project> => {
      
      // Validate data
      validateProjectData(data);
      
      // Security validation for implementors
      if (currentUser?.role === 'implementor' && !data.organizationId) {
        throw new Error('Organization ID is required for implementor users');
      }
      
      // ✅ CRITICAL: Transform data for backend compatibility
      const transformedData = transformProjectForBackend(data);
      
      
      if (!apiClient.projects || !('create' in apiClient.projects)) {
        throw new Error('Project creation not available in current API version');
      }
      
      const response = await (apiClient.projects as any).create(transformedData);
      
      return normalizeSingleResponse<Project>(response);
    },
    options
  );
}

/**
 * Update existing project with proper field mapping
 */
export function useUpdateProject(options?: UseMutationOptions<Project, { id: string; data: ProjectUpdateRequest }>) {
  return useMutation(
    async ({ id, data }: { id: string; data: ProjectUpdateRequest }): Promise<Project> => {
      validateObjectId(id, 'Project ID');
      
      
      // Validate data (allow partial updates)
      if (data.name || data.projectName) {
        validateProjectData(data);
      }
      
      // ✅ CRITICAL: Transform data for backend compatibility
      const transformedData = transformProjectForBackend(data);
      
      
      if (!apiClient.projects || !('update' in apiClient.projects)) {
        throw new Error('Project update not available in current API version');
      }
      
      const response = await (apiClient.projects as any).update(id, transformedData);
      
      return normalizeSingleResponse<Project>(response);
    },
    options
  );
}

/**
 * Delete project
 */
export function useDeleteProject(options?: UseMutationOptions<boolean, string>) {
  return useMutation(
    async (id: string): Promise<boolean> => {
      validateObjectId(id, 'Project ID');
      
      
      if (!apiClient.projects || !('delete' in apiClient.projects)) {
        throw new Error('Project deletion not available in current API version');
      }
      
      const response = await (apiClient.projects as any).delete(id);
      
      return true;
    },
    options
  );
}

/**
 * ✅ RESTORED: Archive project (instead of delete)
 */
export function useArchiveProject(options?: UseMutationOptions<Project, string>) {
  return useMutation(
    async (projectId: string): Promise<Project> => {
      validateObjectId(projectId, 'Project ID');
      
      
      if (!apiClient.projects || !('archive' in apiClient.projects)) {
        throw new Error('Project archiving not available in current API version');
      }
      
      const response = await (apiClient.projects as any).archive(projectId);
      
      return normalizeSingleResponse<Project>(response);
    },
    options
  );
}

/**
 * ✅ RESTORED: Restore archived project
 */
export function useRestoreProject(options?: UseMutationOptions<Project, string>) {
  return useMutation(
    async (projectId: string): Promise<Project> => {
      validateObjectId(projectId, 'Project ID');
      
      
      if (!apiClient.projects || !('restore' in apiClient.projects)) {
        throw new Error('Project restoration not available in current API version');
      }
      
      const response = await (apiClient.projects as any).restore(projectId);
      
      return normalizeSingleResponse<Project>(response);
    },
    options
  );
}

/**
 * Toggle project active status
 */
export function useToggleProjectStatus(options?: UseMutationOptions<Project, { id: string; isActive: boolean }>) {
  return useMutation(
    async ({ id, isActive }: { id: string; isActive: boolean }): Promise<Project> => {
      validateObjectId(id, 'Project ID');
      
      
      if (!apiClient.projects || !('update' in apiClient.projects)) {
        throw new Error('Project status update not available in current API version');
      }
      
      const response = await (apiClient.projects as any).update(id, { isActive });
      
      return normalizeSingleResponse<Project>(response);
    },
    options
  );
}

/**
 * ✅ RESTORED: Update project visibility groups
 */
export function useUpdateProjectVisibility(options?: UseMutationOptions<Project, { projectId: string; visibilityGroups: string[] }>) {
  return useMutation(
    async ({ projectId, visibilityGroups }: { projectId: string; visibilityGroups: string[] }): Promise<Project> => {
      validateObjectId(projectId, 'Project ID');
      
      // Validate visibility groups
      validateObjectIds(visibilityGroups, 'Visibility Group IDs');
      
      
      if (!apiClient.projects || !('get' in apiClient.projects) || !('update' in apiClient.projects)) {
        throw new Error('Project visibility update not available in current API version');
      }
      
      // Get current project data to preserve other fields
      const currentResponse = await (apiClient.projects as any).get(projectId);
      const currentProject = normalizeSingleResponse<Project>(currentResponse);
      
      // Update only the visibilityGroups field while preserving other data
      const updateData: ProjectUpdateRequest = {
        name: currentProject.name,
        description: currentProject.description,
        priority: currentProject.priority,
        isPublic: currentProject.isPublic,
        isActive: currentProject.isActive,
        visibilityGroups: visibilityGroups
      };
      
      const response = await (apiClient.projects as any).update(projectId, updateData);
      
      return normalizeSingleResponse<Project>(response);
    },
    options
  );
}

/**
 * ✅ RESTORED: Batch operations on projects
 */
export function useBatchProjectOperations(options?: UseMutationOptions<any, { operation: 'archive' | 'restore' | 'delete' | 'activate' | 'deactivate'; projectIds: string[] }>) {
  return useMutation(
    async ({ operation, projectIds }: { operation: 'archive' | 'restore' | 'delete' | 'activate' | 'deactivate'; projectIds: string[] }): Promise<any> => {
      if (!projectIds || projectIds.length === 0) {
        throw new Error('At least one project ID is required');
      }
      
      if (!['archive', 'restore', 'delete', 'activate', 'deactivate'].includes(operation)) {
        throw new Error('Invalid operation. Must be archive, restore, delete, activate, or deactivate');
      }
      
      validateObjectIds(projectIds, 'Project IDs');
      
      
      // Check if bulk operations exist, otherwise fall back to individual operations
      if (apiClient.projects && 'bulkOperation' in apiClient.projects) {
        const response = await (apiClient.projects as any).bulkOperation({
          projectIds,
          operation
        });
        return normalizeSingleResponse(response);
      } else {
        // Fallback to individual operations
        const results = [];
        
        for (const projectId of projectIds) {
          try {
            let result;
            switch (operation) {
              case 'archive':
                if (apiClient.projects && 'archive' in apiClient.projects) {
                  result = await (apiClient.projects as any).archive(projectId);
                } else {
                  throw new Error('Project archiving not available');
                }
                break;
              case 'restore':
                if (apiClient.projects && 'restore' in apiClient.projects) {
                  result = await (apiClient.projects as any).restore(projectId);
                } else {
                  throw new Error('Project restoration not available');
                }
                break;
              case 'delete':
                if (apiClient.projects && 'delete' in apiClient.projects) {
                  result = await (apiClient.projects as any).delete(projectId);
                } else {
                  throw new Error('Project deletion not available');
                }
                break;
              case 'activate':
              case 'deactivate':
                if (apiClient.projects && 'update' in apiClient.projects) {
                  result = await (apiClient.projects as any).update(projectId, { 
                    isActive: operation === 'activate' 
                  });
                } else {
                  throw new Error('Project status update not available');
                }
                break;
            }
            
            results.push({ projectId, success: true, result });
          } catch (error: any) {
            results.push({ projectId, success: false, error: error.message });
          }
        }
        
        return results;
      }
    },
    options
  );
}

// ===== UTILITY EXPORTS =====

// Export utility functions for reuse in other hooks
export { 
  normalizeVisibilityGroups,
  transformProjectForBackend,
  validateProjectData
};

// Re-export validation utilities from useApi for convenience
export { validateObjectId, validateObjectIds } from './useApi';

// ===== DEFAULT EXPORT =====

// ✅ RESTORED: Default export with all project hooks for convenience
const projectHooks = {
  // Query hooks
  useProjects,
  useProject,
  useProjectCorrespondences,
  useProjectStats,
  useProjectActivity,
  useSearchProjects,
  useProjectAnalytics,
  
  // Mutation hooks
  useCreateProject,
  useUpdateProject,
  useDeleteProject,
  useArchiveProject,
  useRestoreProject,
  useToggleProjectStatus,
  useUpdateProjectVisibility,
  useBatchProjectOperations
};

export default projectHooks;